import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import SessionContent from "../components/page-components/TeamFocusSession/SessionContent";
import InvalidSession from "../components/page-components/TeamFocusSession/InvalidSession";
import SessionFetchError from "../components/page-components/TeamFocusSession/SessionFetchError";
import { useTeamSession } from "../hooks/useTeamSession";
import SessionLoading from "../components/page-components/TeamFocusSession/SessionLoading";
import PreJoinModal from "../components/page-components/TeamFocusSession/PreJoinModal";
import { VideoOff } from 'lucide-react';

const TeamFocusSessionPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();

  const { sess, loadingSession, fetchError, isSessionInvalid, handlers, retry } =
    useTeamSession(teamId);

  // Pre-join state
  const [showPreJoinModal, setShowPreJoinModal] = useState(true);
  const [joinPreferences, setJoinPreferences] = useState<{ camera: boolean; microphone: boolean } | null>(null);

  const handlePreJoin = useCallback((options: { camera: boolean; microphone: boolean }) => {
    setJoinPreferences(options);
    setShowPreJoinModal(false);
  }, []);

  const navigate = useNavigate();
  const handleGoBack = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  if (!teamId || loadingSession) {
    return <SessionLoading />;
  }

  if (fetchError) {
    return <SessionFetchError onRetry={retry} />;
  }

  if (sess.status === 'ended' || sess.status === 'completed') {
    return (
      <InvalidSession
        icon={<VideoOff className="w-16 h-16 mx-auto text-orange-400 mb-4" />}
        title="Session Ended"
        message="This focus session has already ended. Return to the dashboard to view your session history or start a new session."
        buttonText="Back to Dashboard"
        onButtonClick={handleGoBack}
      />
    );
  }

  // Show pre-join modal before connecting
  if (showPreJoinModal) {
    return (
      <>
        <SessionLoading />
        <PreJoinModal
          isOpen={showPreJoinModal}
          onJoin={handlePreJoin}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh] w-full overflow-hidden bg-gray-100">
      <LiveKitRoom
        className="flex-1 flex flex-col min-h-0"
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        token={sess.rtcToken ?? ''}
        connect={!!sess.rtcToken}
        video={joinPreferences?.camera ?? false}
        audio={joinPreferences?.microphone ?? false}
      >
        <RoomAudioRenderer />
        <SessionContent
          teamId={teamId}
          sess={sess}
          isSessionInvalid={isSessionInvalid}
          handlers={handlers}
          cameraEnabled={joinPreferences?.camera ?? false}
          microphoneEnabled={joinPreferences?.microphone ?? false}
        />
      </LiveKitRoom>
    </div>
  );
};

export default TeamFocusSessionPage;
